import React, {useState} from 'react';
import AnimateHeight from 'react-animate-height';

import {Button, IButtonProps} from '../../components/button';
import {Container} from '../../components/container';
import {Dropdown} from '../../components/dropdown';
import {Feedback, IFeedbackProps} from './../../components/feedback';
import {
    Card,
    CardContent,
    DashboardGeneralCard,
    Grid,
    GridColumn,
    ITemplateGeneralCardProps,
    ITemplatePlainTextCardProps,
    PlainTextCard,
} from '../../components';
import {IQuestionProps, Question} from '../../components/question';
import {IPageTitleAndFiltersProps, PageTitleAndFilters} from '../../modules';

export type TFeedbackDashboardExpanded = 'feedback' | 'segments' | 'companies' | 'questions' | 'no-data' | null;

export interface ITemplateFeedbackDashboardProps {
    pageTitleAndFilters: IPageTitleAndFiltersProps;
    defaultExpanded?: TFeedbackDashboardExpanded;
    expanded?: TFeedbackDashboardExpanded;
    feedback?: ITemplateFeedbackFeedbackProps;
    segments?: ITemplateGeneralCardProps;
    companies?: ITemplateGeneralCardProps;
    questions?: ITemplateFeedbackQuestionsProps;
    noData?: ITemplatePlainTextCardProps;
}

export interface ITemplateFeedbackFeedbackProps extends ITemplateFeedbackCardHeaderProps {
    data: IFeedbackProps;
}

export interface ITemplateFeedbackCardHeaderProps {
    title: string;
    actions?: IButtonProps[];
    more?: IButtonProps;
}

export interface ITemplateFeedbackQuestionsProps {
    items: IQuestionProps[];
    title?: string;
    button?: IButtonProps;
}

const TemplateFeedbackDashboard = (props: ITemplateFeedbackDashboardProps): JSX.Element => {
    const [expanded, setExpanded] = useState<TFeedbackDashboardExpanded>(props.defaultExpanded ? props.defaultExpanded : null);

    const renderActions: (actions: IButtonProps[]) => JSX.Element = (actions: IButtonProps[]) => {
        if (actions.length === 1) {
            return <Button {...actions[0]} color="white" icon="info" style="link"/>;
        }

        return (
            <Dropdown
                placement="bottom"
                triggerType="click"
            >
                <div className="text">
                    {actions.map((action: IButtonProps, i: number) => (
                        <div key={'history-action-' + i}>
                            <Button {...action} style="link"/>
                        </div>
                    ))}
                </div>
            </Dropdown>
        );
    };

    const getExpandedColumn = (name?: TFeedbackDashboardExpanded): string[] => {
        if (expanded && name === expanded) {
            return ['lg-12'];
        } else if (expanded) {
            return ['0'];
        } else if (name === 'no-data') {
            return ['lg-12'];
        } else if (name === 'questions') {
            return ['lg-12'];
        } else {
            return ['lg-6'];
        }
    };

    const getAnimatedHeight = (name?: TFeedbackDashboardExpanded): string | number => {
        if (expanded && name !== expanded) {
            return 0;
        } else {
            return 'auto';
        }
    };

    const renderCardHeader = (headerProps: ITemplateFeedbackCardHeaderProps, name?: TFeedbackDashboardExpanded): JSX.Element => (
        <Grid noWrap={['sm']} align={['middle-xs']}>
            <GridColumn width={['xs-8', 'max-sm']}>
                <h3>{headerProps.title}</h3>
            </GridColumn>
            {(headerProps.actions || headerProps.more) &&
            <GridColumn width={['xs-4', 'min-sm']}>
                <Grid align={['middle-xs']} justify={['end-xs']}>
                    {headerProps.actions &&
                    <GridColumn width={['min-xs']}>
                        {renderActions(headerProps.actions)}
                    </GridColumn>
                    }
                    {headerProps.more && name &&
                    <GridColumn width={['xs']}>
                        <Button
                            {...headerProps.more}
                            color="white"
                            icon={expanded === name ? 'collapse' : 'expand'}
                            onClick={(event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>): void => {
                                event.preventDefault();

                                setExpanded(expanded === name ? null : name);

                                if (headerProps.more && headerProps.more.onClick) {
                                    headerProps.more.onClick(event);
                                }
                            }}
                            style="link"
                        />
                    </GridColumn>
                    }
                </Grid>
            </GridColumn>
            }
        </Grid>
    );

    const renderFeedbackCard = (feedback: ITemplateFeedbackFeedbackProps, name: TFeedbackDashboardExpanded): JSX.Element => (
        <GridColumn width={getExpandedColumn(name)}>
            <Card>
                <AnimateHeight className="h-animate-height" height={getAnimatedHeight(name)}>
                    <CardContent>
                        <div className="text">
                            {renderCardHeader(feedback, name)}
                            <Feedback {...feedback.data} aspectRatio={expanded !== name}/>
                        </div>
                    </CardContent>
                </AnimateHeight>
            </Card>
        </GridColumn>
    );

    const renderNoDataCard = (cardProps: ITemplatePlainTextCardProps): JSX.Element => (
        <GridColumn width={getExpandedColumn('no-data')}>
            <Card>
                <AnimateHeight className="h-animate-height" height={getAnimatedHeight()}>
                    <PlainTextCard text={cardProps.text}/>
                </AnimateHeight>
            </Card>
        </GridColumn>
    );

    const renderQuestions = (questions: ITemplateFeedbackQuestionsProps): JSX.Element => (
        <GridColumn width={getExpandedColumn('questions')}>
            <AnimateHeight className="h-animate-height" height={getAnimatedHeight()}>
                <div className="text text--double">
                    {questions.title && <h3>{questions.title}</h3>}
                    <Grid equalHeight={true} wrap={['xs']}>
                        {questions.items.map((el: IQuestionProps, i: number) => (
                            <GridColumn width={['xs-12', 'lg-6', 'xl-4']} key={i}>
                                <Question {...el} />
                            </GridColumn>
                        ))}
                    </Grid>
                    {questions.button &&
                    <Grid align={['center-xs']}>
                        <GridColumn width={['xs']}>
                            <Button {...questions.button} color="white"/>
                        </GridColumn>
                    </Grid>
                    }
                </div>
            </AnimateHeight>
        </GridColumn>
    );

    const renderGeneralCard = (cardProps: ITemplateGeneralCardProps, name: TFeedbackDashboardExpanded): JSX.Element => (
        <GridColumn width={getExpandedColumn(name)}>
            <Card>
                <AnimateHeight className="h-animate-height" height={getAnimatedHeight(name)}>
                    <DashboardGeneralCard
                        {...cardProps}
                        isExpanded={expanded === name}
                        setExpanded={(expandedValue): void => setExpanded(expandedValue ? name : null)}
                    />
                </AnimateHeight>
            </Card>
        </GridColumn>
    );

    return (
        <Container>
            <div className="text text--triple">
                <PageTitleAndFilters {...props.pageTitleAndFilters}/>
                <Grid wrap={['xs']} equalHeight={true} transition={true}>
                    {props.companies && renderGeneralCard(props.companies, 'companies')}
                    {props.feedback && renderFeedbackCard(props.feedback, 'feedback')}
                    {props.segments && renderGeneralCard(props.segments, 'segments')}
                    {props.questions && renderQuestions(props.questions)}
                    {props.noData && renderNoDataCard(props.noData)}
                </Grid>
            </div>
        </Container>
    );
};

export default TemplateFeedbackDashboard;
